// ============================================
// DATETIME PICKER - Alpine.js Component
// A field that opens a dropdown next to it with a calendar and a 12-hour
// clock (a.m./p.m.) whose minutes move in `step` increments.
// The value keeps the native inputs' format, so it replaces
// <input type="datetime-local|date|time"> as is:
//   datetime 'YYYY-MM-DDTHH:mm' · date 'YYYY-MM-DD' · time 'HH:mm' · '' when empty
// Markup and parameters: woocommerce/myaccount/components/common/datetime-picker.php
// ============================================

import Alpine from 'alpinejs';

type Mode = 'datetime' | 'date' | 'time';

interface DateTimePickerConfig {
    mode?: Mode;
    step?: number;
    min?: string;
    max?: string;
    triggerId: string;
    panelId: string;
}

interface Parts {
    y: number;
    m: number; // 0-11
    d: number;
    h: number; // 0-23
    i: number; // minutes
}

interface DayCell {
    key: number;
    iso: string;
    day: number;
    inMonth: boolean;
    today: boolean;
    selected: boolean;
    disabled: boolean;
    tabbable: boolean;
    label: string;
}

// Minutes between selectable times; each one splits the hour into whole steps.
const STEPS = [1, 5, 10, 15, 20, 30];
const DEFAULT_STEP = 5;
// Dial: 240px wide, the numbers centred on this radius.
const DIAL_CENTER = 120;
const DIAL_RADIUS = 92;
// The dropdown keeps this distance from the field and from the screen edges.
const GAP = 6;
const MARGIN = 8;
const YEARS_PER_PAGE = 24;

const pad = (n: number) => String(n).padStart(2, '0');
const isoDate = (y: number, m: number, d: number) => y + '-' + pad(m + 1) + '-' + pad(d);
const daysIn = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function parseDate(iso: string): { y: number; m: number; d: number } | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return match ? { y: +match[1], m: +match[2] - 1, d: +match[3] } : null;
}

// Mexican Spanish or US English, after the page's language (/es/...).
function locale(): string {
    const lang = (Alpine.store('i18n') as any)?.currentLang || 'en';
    return lang === 'es' ? 'es-MX' : 'en-US';
}

function formatter(options: Intl.DateTimeFormatOptions) {
    return new Intl.DateTimeFormat(locale(), options);
}

// 0 = Sunday. Browsers without Intl week info get Sunday, as in Mexico and the US.
function firstWeekDay(): number {
    try {
        const info = new (Intl as any).Locale(locale());
        const week = info.getWeekInfo?.() ?? info.weekInfo;
        return week?.firstDay ? week.firstDay % 7 : 0;
    } catch (e) {
        return 0;
    }
}

export function registerDateTimePicker() {
    Alpine.data('datetimePicker', (config: DateTimePickerConfig) => {
        const mode: Mode = config.mode ?? 'datetime';
        const step = STEPS.includes(Number(config.step)) ? Number(config.step) : DEFAULT_STEP;
        // Minute numbers on the dial: every step, or every 5 when any minute is allowed.
        const minuteMarks = step === 1 ? 5 : step;
        const min = config.min || '';
        const max = config.max || '';

        // Not reactive: the component root and the open dropdown's listeners.
        let root: HTMLElement | null = null;
        let detach: (() => void) | null = null;
        let dialing = false;

        const inRange = (iso: string) => (!min || iso >= min) && (!max || iso <= max);
        const overlaps = (from: string, to: string) => (!min || to >= min) && (!max || from <= max);
        const panel = () => document.getElementById(config.panelId);

        return {
            // Exposed to the parent through x-modelable.
            value: '',
            open: false,
            placed: false,
            tab: (mode === 'time' ? 'time' : 'date') as 'date' | 'time',
            view: 'days' as 'days' | 'months' | 'years',
            dial: 'hours' as 'hours' | 'minutes',
            draft: { y: 2000, m: 0, d: 1, h: 0, i: 0 } as Parts,
            viewY: 2000,
            viewM: 0,
            yearFrom: 2000,
            focusIso: '',
            pos: { top: 0, left: 0, above: false },

            init() {
                root = this.$el as HTMLElement;
                this.setDraft(this.defaults());
            },

            destroy() {
                detach?.();
                detach = null;
            },

            // ---------------------------------------------------------------- value

            parse(value: string): Parts | null {
                if (mode === 'time') {
                    const match = /^(\d{2}):(\d{2})/.exec(value || '');
                    if (!match) return null;
                    const today = new Date();
                    return { y: today.getFullYear(), m: today.getMonth(), d: today.getDate(), h: +match[1], i: +match[2] };
                }
                const date = parseDate(value);
                if (!date) return null;
                const time = /T(\d{2}):(\d{2})/.exec(value);
                return { ...date, h: time ? +time[1] : 0, i: time ? +time[2] : 0 };
            },

            format(parts: Parts): string {
                const date = isoDate(parts.y, parts.m, parts.d);
                const time = pad(parts.h) + ':' + pad(parts.i);
                return mode === 'date' ? date : mode === 'time' ? time : date + 'T' + time;
            },

            // Now, moved up to the next step; the day stays inside min/max.
            defaults(): Parts {
                const now = new Date();
                now.setSeconds(0, 0);
                now.setMinutes(now.getMinutes() + (step - now.getMinutes() % step) % step);
                const parts = { y: now.getFullYear(), m: now.getMonth(), d: now.getDate(), h: now.getHours(), i: now.getMinutes() };
                const iso = isoDate(parts.y, parts.m, parts.d);
                const bound = min && iso < min ? min : (max && iso > max ? max : '');
                return bound ? { ...parts, ...parseDate(bound)! } : parts;
            },

            setDraft(parts: Parts) {
                this.draft = { ...parts };
                this.viewY = parts.y;
                this.viewM = parts.m;
                this.focusIso = isoDate(parts.y, parts.m, parts.d);
            },

            // What the field shows: "14 oct 2026, 9:05 p.m."
            get display(): string {
                const parts = this.parse(this.value);
                if (!parts) return '';
                const day: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
                const time: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
                const options = mode === 'date' ? day : mode === 'time' ? time : { ...day, ...time };
                return formatter(options).format(new Date(parts.y, parts.m, parts.d, parts.h, parts.i));
            },

            // ---------------------------------------------------------------- open / close

            toggle() {
                if (this.open) {
                    this.close();
                } else {
                    this.show();
                }
            },

            show() {
                this.setDraft(this.parse(this.value) ?? this.defaults());
                this.tab = mode === 'time' ? 'time' : 'date';
                this.view = 'days';
                this.dial = 'hours';
                this.placed = false;
                this.open = true;
                this.attach();
                this.$nextTick(() => {
                    this.place();
                    // Hidden until placed, and a hidden element takes no focus.
                    this.$nextTick(() => this.focusInside());
                });
            },

            close(refocus = true) {
                if (!this.open) return;
                this.open = false;
                dialing = false;
                detach?.();
                detach = null;
                if (refocus) {
                    document.getElementById(config.triggerId)?.focus({ preventScroll: true });
                }
            },

            apply() {
                this.value = this.format(this.draft);
                this.close();
                this.picked();
            },

            clear() {
                this.value = '';
                this.close();
                this.picked();
            },

            // Fired from the root once x-model has passed the value up, so the handler reads the new one.
            picked() {
                const value = this.value;
                this.$nextTick(() => {
                    root?.dispatchEvent(new CustomEvent('datetime-picked', { bubbles: true, detail: { value } }));
                });
            },

            // While open: it follows the field and Escape closes only the dropdown.
            attach() {
                const place = () => this.place();
                const onKey = (event: KeyboardEvent) => {
                    if (event.key !== 'Escape') return;
                    // Captured before the modals' global handler, which would close the modal underneath.
                    event.stopPropagation();
                    event.preventDefault();
                    this.close();
                };
                const element = panel();
                const observer = element && 'ResizeObserver' in window ? new ResizeObserver(place) : null;
                if (element && observer) observer.observe(element);
                window.addEventListener('resize', place);
                window.addEventListener('scroll', place, true);
                window.addEventListener('keydown', onKey, true);
                detach = () => {
                    observer?.disconnect();
                    window.removeEventListener('resize', place);
                    window.removeEventListener('scroll', place, true);
                    window.removeEventListener('keydown', onKey, true);
                };
            },

            // Below the field; above it when it does not fit; otherwise as close as the screen allows.
            place() {
                const trigger = document.getElementById(config.triggerId);
                const element = panel();
                if (!this.open || !trigger || !element) return;

                const field = trigger.getBoundingClientRect();
                const width = element.offsetWidth;
                const height = element.offsetHeight;
                const screenWidth = document.documentElement.clientWidth;
                const screenHeight = window.innerHeight;

                let top: number;
                let above = false;
                if (field.bottom + GAP + height <= screenHeight - MARGIN) {
                    top = field.bottom + GAP;
                } else if (field.top - GAP - height >= MARGIN) {
                    top = field.top - GAP - height;
                    above = true;
                } else {
                    top = Math.max(MARGIN, screenHeight - MARGIN - height);
                }
                const left = Math.max(MARGIN, Math.min(field.left, screenWidth - MARGIN - width));

                this.pos = { top, left, above };
                this.placed = true;
            },

            // The selected day, or the dial on the clock.
            focusInside() {
                const element = panel();
                const target = this.tab === 'time'
                    ? element?.querySelector('[data-dial]')
                    : element?.querySelector('[data-iso="' + this.tabDay + '"]');
                (target as HTMLElement | null)?.focus({ preventScroll: true });
            },

            showDate() {
                this.tab = 'date';
                this.view = 'days';
                this.viewY = this.draft.y;
                this.viewM = this.draft.m;
                this.$nextTick(() => this.focusInside());
            },

            showTime(dial: 'hours' | 'minutes') {
                this.tab = 'time';
                this.dial = dial;
                this.$nextTick(() => this.focusInside());
            },

            // ---------------------------------------------------------------- header

            // "Mié 14 de oct"
            get headerDate(): string {
                return capitalize(formatter({ weekday: 'short', day: 'numeric', month: 'short' })
                    .format(new Date(this.draft.y, this.draft.m, this.draft.d)));
            },

            get headerHour(): string {
                return String(this.draft.h % 12 || 12);
            },

            get headerMinute(): string {
                return pad(this.draft.i);
            },

            get isPm(): boolean {
                return this.draft.h >= 12;
            },

            // ['a.m.', 'p.m.'] or ['AM', 'PM'], after the page's language.
            get periods(): string[] {
                const period = (hour: number) => formatter({ hour: 'numeric', hour12: true })
                    .formatToParts(new Date(2026, 0, 1, hour))
                    .find((part) => part.type === 'dayPeriod')?.value ?? (hour < 12 ? 'AM' : 'PM');
                return [period(9), period(21)];
            },

            setPeriod(pm: boolean) {
                if (pm !== this.isPm) {
                    this.draft.h = (this.draft.h + 12) % 24;
                }
            },

            // ---------------------------------------------------------------- calendar

            get weekdays(): string[] {
                const first = firstWeekDay();
                const names = formatter({ weekday: 'short' });
                // 2026-10-11 is a Sunday.
                return Array.from({ length: 7 }, (_, k) => names.format(new Date(2026, 9, 11 + (first + k) % 7)).replace('.', ''));
            },

            // "Octubre de 2026", the year, or the range of years on display.
            get navTitle(): string {
                if (this.view === 'years') return this.yearFrom + ' – ' + (this.yearFrom + YEARS_PER_PAGE - 1);
                if (this.view === 'months') return String(this.viewY);
                return capitalize(formatter({ month: 'long', year: 'numeric' }).format(new Date(this.viewY, this.viewM, 1)));
            },

            // The day the keyboard lands on: the focused one if it is in view, else the selected one or the 1st.
            get tabDay(): string {
                const prefix = this.viewY + '-' + pad(this.viewM + 1) + '-';
                if (this.focusIso.startsWith(prefix)) return this.focusIso;
                const selected = isoDate(this.draft.y, this.draft.m, this.draft.d);
                return selected.startsWith(prefix) ? selected : prefix + '01';
            },

            // Six weeks, so the dropdown keeps its height from one month to the next.
            get cells(): DayCell[] {
                const offset = (new Date(this.viewY, this.viewM, 1).getDay() - firstWeekDay() + 7) % 7;
                const now = new Date();
                const today = isoDate(now.getFullYear(), now.getMonth(), now.getDate());
                const selected = isoDate(this.draft.y, this.draft.m, this.draft.d);
                const target = this.tabDay;
                const full = formatter({ dateStyle: 'full' });
                return Array.from({ length: 42 }, (_, k) => {
                    const date = new Date(this.viewY, this.viewM, 1 - offset + k);
                    const iso = isoDate(date.getFullYear(), date.getMonth(), date.getDate());
                    return {
                        key: k,
                        iso,
                        day: date.getDate(),
                        inMonth: date.getMonth() === this.viewM,
                        today: iso === today,
                        selected: iso === selected,
                        disabled: !inRange(iso),
                        tabbable: iso === target,
                        label: full.format(date),
                    };
                });
            },

            get months() {
                const names = formatter({ month: 'short' });
                return Array.from({ length: 12 }, (_, m) => ({
                    m,
                    label: capitalize(names.format(new Date(2026, m, 1)).replace('.', '')),
                    selected: this.viewY === this.draft.y && m === this.draft.m,
                    disabled: !overlaps(isoDate(this.viewY, m, 1), isoDate(this.viewY, m, daysIn(this.viewY, m))),
                }));
            },

            get years() {
                const current = new Date().getFullYear();
                return Array.from({ length: YEARS_PER_PAGE }, (_, k) => {
                    const y = this.yearFrom + k;
                    return { y, current: y === current, selected: y === this.draft.y, disabled: !overlaps(y + '-01-01', y + '-12-31') };
                });
            },

            // Days → years → months → days.
            toggleView() {
                if (this.view === 'days') {
                    this.yearFrom = this.viewY - 10;
                    this.view = 'years';
                } else {
                    this.view = 'days';
                }
            },

            shift(delta: number) {
                if (this.view === 'days') {
                    const date = new Date(this.viewY, this.viewM + delta, 1);
                    this.viewY = date.getFullYear();
                    this.viewM = date.getMonth();
                } else if (this.view === 'months') {
                    this.viewY += delta;
                } else {
                    this.yearFrom += delta * YEARS_PER_PAGE;
                }
            },

            canShift(delta: number): boolean {
                if (this.view === 'days') {
                    const date = new Date(this.viewY, this.viewM + delta, 1);
                    const y = date.getFullYear();
                    const m = date.getMonth();
                    return overlaps(isoDate(y, m, 1), isoDate(y, m, daysIn(y, m)));
                }
                if (this.view === 'months') {
                    const y = this.viewY + delta;
                    return overlaps(y + '-01-01', y + '-12-31');
                }
                const from = this.yearFrom + delta * YEARS_PER_PAGE;
                return overlaps(from + '-01-01', (from + YEARS_PER_PAGE - 1) + '-12-31');
            },

            pickYear(y: number) {
                this.viewY = y;
                this.view = 'months';
            },

            pickMonth(m: number) {
                this.viewM = m;
                this.view = 'days';
                this.$nextTick(() => this.focusInside());
            },

            // With a time to pick, the clock follows.
            pickDay(cell: DayCell) {
                if (cell.disabled) return;
                Object.assign(this.draft, parseDate(cell.iso));
                this.focusIso = cell.iso;
                if (mode === 'datetime') {
                    this.showTime('hours');
                }
            },

            // Arrows move a day or a week, PageUp/PageDown a month, Home/End to the week's ends.
            dayKey(event: KeyboardEvent) {
                const current = parseDate(this.tabDay)!;
                const date = new Date(current.y, current.m, current.d);
                const weekPosition = (date.getDay() - firstWeekDay() + 7) % 7;
                const moves: Record<string, number> = {
                    ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -weekPosition, End: 6 - weekPosition,
                };

                if (event.key in moves) {
                    date.setDate(date.getDate() + moves[event.key]);
                } else if (event.key === 'PageUp' || event.key === 'PageDown') {
                    const month = new Date(current.y, current.m + (event.key === 'PageUp' ? -1 : 1), 1);
                    month.setDate(Math.min(current.d, daysIn(month.getFullYear(), month.getMonth())));
                    date.setTime(month.getTime());
                } else {
                    return;
                }

                event.preventDefault();
                this.focusIso = isoDate(date.getFullYear(), date.getMonth(), date.getDate());
                this.viewY = date.getFullYear();
                this.viewM = date.getMonth();
                this.$nextTick(() => this.focusInside());
            },

            // ---------------------------------------------------------------- clock

            dialSpot(angle: number) {
                const radians = angle * Math.PI / 180;
                return {
                    left: (DIAL_CENTER + DIAL_RADIUS * Math.sin(radians)) + 'px',
                    top: (DIAL_CENTER - DIAL_RADIUS * Math.cos(radians)) + 'px',
                };
            },

            get dialItems() {
                if (this.dial === 'hours') {
                    const current = this.draft.h % 12 || 12;
                    return Array.from({ length: 12 }, (_, k) => {
                        const hour = k || 12;
                        return { key: 'h' + hour, label: String(hour), selected: hour === current, style: this.dialSpot(k * 30) };
                    });
                }
                return Array.from({ length: 60 / minuteMarks }, (_, k) => {
                    const minute = k * minuteMarks;
                    return { key: 'm' + minute, label: pad(minute), selected: minute === this.draft.i, style: this.dialSpot(minute * 6) };
                });
            },

            get handAngle(): number {
                return this.dial === 'hours' ? (this.draft.h % 12) * 30 : this.draft.i * 6;
            },

            // Pivots on the dial's centre and reaches the numbers.
            get handStyle() {
                return { height: DIAL_RADIUS + 'px', transform: 'translate(-50%, -100%) rotate(' + this.handAngle + 'deg)' };
            },

            // The hand ends on a number unless the minute sits between two (any-minute step).
            get handOnNumber(): boolean {
                return this.dial === 'hours' || this.draft.i % minuteMarks === 0;
            },

            get dialText(): string {
                return this.dial === 'hours'
                    ? this.headerHour + ' ' + this.periods[this.isPm ? 1 : 0]
                    : this.headerMinute;
            },

            dialDown(event: PointerEvent) {
                const dial = event.currentTarget as HTMLElement;
                dial.setPointerCapture?.(event.pointerId);
                dial.focus({ preventScroll: true });
                dialing = true;
                this.dialAt(event);
            },

            dialMove(event: PointerEvent) {
                if (dialing) this.dialAt(event);
            },

            // Once the hour is set, the minutes follow.
            dialUp(event: PointerEvent) {
                if (!dialing) return;
                dialing = false;
                this.dialAt(event);
                if (this.dial === 'hours') {
                    this.dial = 'minutes';
                }
            },

            dialCancel() {
                dialing = false;
            },

            // The pointer's angle around the centre, snapped to an hour or to a step.
            dialAt(event: PointerEvent) {
                const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
                const x = event.clientX - box.left - box.width / 2;
                const y = event.clientY - box.top - box.height / 2;
                const angle = (Math.atan2(x, -y) * 180 / Math.PI + 360) % 360;
                if (this.dial === 'hours') {
                    this.draft.h = Math.round(angle / 30) % 12 + (this.isPm ? 12 : 0);
                } else {
                    this.draft.i = Math.round(angle / 6 / step) * step % 60;
                }
            },

            // Arrows move an hour or a step; Enter goes on to the minutes, then accepts.
            dialKey(event: KeyboardEvent) {
                const moves: Record<string, number> = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1 };
                if (event.key in moves) {
                    event.preventDefault();
                    const delta = moves[event.key];
                    if (this.dial === 'hours') {
                        this.draft.h = (this.draft.h % 12 + delta + 12) % 12 + (this.isPm ? 12 : 0);
                    } else {
                        const base = Math.round(this.draft.i / step) * step;
                        this.draft.i = (base + delta * step + 60) % 60;
                    }
                } else if (event.key === 'Enter') {
                    event.preventDefault();
                    if (this.dial === 'hours') {
                        this.dial = 'minutes';
                    } else {
                        this.apply();
                    }
                }
            },
        };
    });
}
